import { FC, useCallback, useState } from 'react';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { Adjustment } from '../payrun.model';
import ImagePreview from 'components/image-preview/ImagePreview';
import { BinIcon, FormEditIcon, PlusIconBlue } from 'assets/icons';
import { GenericDeleteModal } from 'components';
import { useDeleteMerchantAdjustmentMutation, useUpdateAdjustmentMutation } from '../api';
import { AdjustmentSourceType } from '../constants';
import AddAdjustmentModal, { CreateAdjustmentRequest } from './AddAdjustmentModal';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { PayRunsCleverTapEvents } from '../events';
import { logAnalyticEvent } from 'utils/analytics';

interface OtherAdjustmentsTableProps {
  adjustments: Adjustment[];
  totalAdjustmentAmount: string;
  onAddEarning?: () => void;
  onAddDeduction?: () => void;
  hasManagePayAdjustments: boolean;
}

const OtherAdjustmentsTable: FC<OtherAdjustmentsTableProps> = ({
  adjustments,
  totalAdjustmentAmount,
  onAddEarning,
  onAddDeduction,
  hasManagePayAdjustments
}) => {
  const [previewImages, setPreviewImages] = useState<{
    isOpen: boolean;
    images: Array<{ url: string; urlLink: string; title: string }>;
    selectedIndex: number;
  }>({
    isOpen: false,
    images: [],
    selectedIndex: 0
  });
  const [selectedAdjustment, setSelectedAdjustment] = useState<Adjustment | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [updateAdjustment] = useUpdateAdjustmentMutation();
  const [deleteAdjustment] = useDeleteMerchantAdjustmentMutation();
  const handleViewReceipt = useCallback((adjustment: Adjustment) => {
    if (adjustment.receiptUrl) {
      setPreviewImages({
        isOpen: true,
        images: [
          {
            url: adjustment.receiptUrl,
            urlLink: adjustment.receiptUrl,
            title: adjustment.title
          }
        ],
        selectedIndex: 0
      });
    }
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewImages(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleEditAdjustment = (adjustment: Adjustment) => {
    setSelectedAdjustment(adjustment);
    setIsEditModalOpen(true);
  };

  const handleSaveEditedAdjustment = async (adjustmentData: CreateAdjustmentRequest) => {
    await updateAdjustment({
      adjustmentId: selectedAdjustment?.id,
      merchantId: selectedAdjustment?.merchantId,
      teamId: selectedAdjustment?.teamId,
      postedDate: adjustmentData?.date,
      notes: adjustmentData?.description,
      title: adjustmentData?.title,
      amount: adjustmentData?.amount,
      receiptUrl: adjustmentData?.receiptUrl
    });

    setIsEditModalOpen(false);
    setSelectedAdjustment(null);
  };

  const handleDeleteAdjustment = (adjustment: Adjustment) => {
    setSelectedAdjustment(adjustment);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedAdjustment) {
      try {
        await deleteAdjustment({
          teamId: selectedAdjustment.teamId,
          merchantId: selectedAdjustment.merchantId,
          adjustmentId: selectedAdjustment.id
        });
        setIsDeleteModalOpen(false);
        logAnalyticEvent(PayRunsCleverTapEvents.webPayRunAdjustmentRemoved, {
          merchantId: selectedAdjustment.merchantId,
          source: 'pay-run-adjustments'
        });
      } catch (error) {
        // Error handling is done by the API's extraOptions
      }
    }
  };

  const columns = [
    { header: 'Adjustment', width: '20%' },
    { header: 'Date of posting', width: '15%' },
    { header: 'Notes', width: '20%' },
    { header: 'Related', width: '20%' },
    { header: 'Amount', width: '15%' },
    { header: '', width: '10%' }
  ];

  return (
    <div className="flex w-full flex-col rounded-md border border-[#D8DFE5]">
      {/* Header row */}
      <div
        className="grid w-full grid-cols-[15%_15%_15%_15%_15%_10%] 
        space-x-4 border-b border-[#ECF1F7] bg-[#F8F8F8] py-3 px-6">
        {columns.map(column => (
          <div key={column.header} className="text-sbase font-semibold text-[#828282]">
            {column.header}
          </div>
        ))}
      </div>

      {/* Table content */}
      <div className="flex flex-col divide-y divide-[#ECF1F7]">
        {adjustments.length > 0 ? (
          adjustments.map(adjustment => (
            <div
              key={adjustment.id}
              className="grid grid-cols-[15%_15%_15%_15%_15%_10%] items-center space-x-4 bg-white py-4 px-6">
              <div className="text-sbase font-medium text-primaryText line-clamp-2">{adjustment.title}</div>

              <div className="text-sbase font-medium text-primaryText">
                {DateUtils.getDateInFormat({
                  date: new Date(adjustment.postedDate),
                  dateFormat: 'dd MMM yyyy'
                })}
              </div>
              <TooltipWrapper tooltip={adjustment.notes || ''} showTooltip={Boolean(adjustment.notes)}>
                <div className="whitespace-pre-wrap text-sbase font-medium text-primaryText line-clamp-3">
                  {adjustment.notes || '-'}
                </div>
              </TooltipWrapper>

              <div>
                {adjustment.receiptUrl ? (
                  <button
                    onClick={() => handleViewReceipt(adjustment)}
                    className="text-sbase font-semibold text-secondary hover:underline focus:outline-none"
                    aria-label={`View receipt for ${adjustment.title}`}
                    tabIndex={0}>
                    View receipt
                  </button>
                ) : (
                  <div className="text-sbase font-medium text-primaryText">-</div>
                )}
              </div>
              <div className="text-sbase font-bold text-primaryText">{getAmountWithCurrency(adjustment.amount)}</div>
              <div className="flex items-center justify-end gap-2">
                {hasManagePayAdjustments && (
                  <>
                    <button
                      onClick={() => handleEditAdjustment(adjustment)}
                      className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 focus:outline-none"
                      aria-label={`Edit ${adjustment.title}`}
                      tabIndex={0}>
                      <FormEditIcon className="path-fill-current h-4 w-4 text-primaryText" />
                    </button>
                    <button
                      onClick={() => handleDeleteAdjustment(adjustment)}
                      className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 focus:outline-none"
                      aria-label={`Delete ${adjustment.title}`}
                      tabIndex={0}>
                      <BinIcon className="path-fill-current h-4 w-4 text-primaryText" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex justify-center py-6 text-sbase text-[#4F4F4F]">No adjustments available</div>
        )}

        {/* Add earning and Add deduction buttons */}
        {hasManagePayAdjustments && (
          <div className="flex items-center gap-8 bg-white py-4 px-6">
            <button
              onClick={onAddEarning}
              className="flex items-center gap-2 text-secondary hover:opacity-80 focus:outline-none"
              aria-label="Add earning"
              tabIndex={0}>
              <PlusIconBlue className="h-6 w-6" />
              <span className="text-17px font-semibold">Add earning</span>
            </button>

            <button
              onClick={onAddDeduction}
              className="flex items-center gap-2 text-secondary hover:opacity-80 focus:outline-none"
              aria-label="Add deduction"
              tabIndex={0}>
              <PlusIconBlue className="h-6 w-6" />
              <span className="text-17px font-semibold">Add deduction</span>
            </button>
          </div>
        )}
      </div>

      {/* Total row */}
      <div className="grid grid-cols-[60%_40%] gap-4 rounded-b-md bg-[rgba(236,241,247,0.5)] py-[10px] px-6">
        <div className="mr-3 text-right text-sbase font-bold tracking-[-0.02em] text-primaryText">Total adjustment</div>
        <div className=" text-sbase font-bold tracking-[-0.02em] text-primaryText">
          {getAmountWithCurrency(totalAdjustmentAmount)}
        </div>
      </div>

      {/* Image Preview */}
      {previewImages.isOpen && (
        <ImagePreview
          images={previewImages.images}
          selectedIndex={previewImages.selectedIndex}
          onClose={handleClosePreview}
        />
      )}

      {isDeleteModalOpen && selectedAdjustment && (
        <GenericDeleteModal
          isActionInProgress={false}
          onPrimaryBtnClick={handleConfirmDelete}
          onSecondaryBtnClick={() => setIsDeleteModalOpen(false)}
          primaryBtnId="delete-adjustment-confirm"
          primaryBtnLabel="Yes, Delete"
          secondaryBtnId="delete-adjustment-cancel"
          secondaryBtnLabel="No, Cancel"
          showModal={true}
          subTitle=""
          title="Are you sure you want to delete this adjustment?"
        />
      )}

      {isEditModalOpen && selectedAdjustment && (
        <AddAdjustmentModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveEditedAdjustment}
          isEdit={true}
          selectedAdjustment={selectedAdjustment}
          sourceType={AdjustmentSourceType.MERCHANT}
          assignees={[]}
        />
      )}
    </div>
  );
};

export default OtherAdjustmentsTable;
